import React, { useState, useRef } from 'react';
import Modal from '../Modal/Modal';
import { downloadBlob } from '../../utils/fileHelper';
import { exportProfileCardToPDF } from '../../utils/exportUtils';
import { useConstants } from '../../constant/constantProvider';
import { getNormalizedCareerTimeline } from '../../utils/talentPulseUtils';
import './TalentPulseProfileCard.css';

const buildProfile = ({ details, company, status, experience, source, candidateProfile = {} }) => {
    const role = details[0]?.text || 'Candidate profile';
    const location = details.find((detail) => detail.icon)?.text?.replace(/^📍\s*/, '') || 'Location not available';

    return {
        role,
        location,
        current: company || '—',
        currentOrg: `${company || 'Current organisation'} · ${location.split(' · ')[0]}`,
        target: 'Details to be discussed',
        interviewDate: source === 'Interview' ? 'Interview completed' : 'Awaiting interview',
        insights: [
            `Experience: ${experience || 'Not available'}`,
            `Current organisation: ${company || 'Not available'}`,
            `Profile source: ${source || 'Not available'}`
        ],
        strategy: [
            'Detailed interview assessment will appear here',
            'Candidate strengths and territory fit',
            'Recommended next steps for the hiring panel'
        ],
        quote: 'Candidate interview notes will appear here.',
        nextStep: status === 'Interview Done' ? 'Panel feedback awaited' : 'Review candidate profile',
        ...candidateProfile
    };
};

const ProfileSummaryItem = ({ label, value }) => (
    <div className="tpc-profile-summary-item">
        <div className="tpc-profile-summary-label">{label}</div>
        <div className="tpc-profile-summary-value">{value}</div>
    </div>
);

const ProfileListSection = ({ title, items }) => (
    <section className="tpc-profile-section">
        <h3>{title}</h3>
        <ul>
            {items.map((item) => <li key={item}>{item}</li>)}
        </ul>
    </section>
);

const ProfileKeyValueRow = ({ label, value }) => (
    <div className="tpc-profile-key-value">
        <div>{label}</div>
        <div className="tpc-profile-key-value-value">{value}</div>
    </div>
);

const TalentPulseProfileCard = ({
    id,
    initials,
    name,
    candidateId,
    details = [],
    company,
    status,
    experience,
    source,
    profile: candidateProfile = {},
    checked = false,
    onCheckChange = () => { },
    onViewProfile
}) => {
    const constants = useConstants();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const modalContentRef = useRef(null);

    const profile = buildProfile({ details, company, status, experience, source, candidateProfile });
    const summaryItems = [
        { label: 'ID', value: candidateId || '—' },
        { label: 'Status', value: status || '—' },
        { label: 'Interview', value: profile.interviewDate },
        { label: 'Experience', value: experience || '—' },
        { label: 'Current', value: profile.current }
    ];

    const handleViewProfileClick = () => {
        // Open modal immediately — don't wait for API
        setIsModalOpen(true);
        if (onViewProfile) {
            setIsProfileLoading(true);
            onViewProfile(candidateId || id).finally(() => {
                setIsProfileLoading(false);
            });
        }
    };

    const handleCloseModal = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        setIsModalOpen(false);
    };

    const handleDownloadProfile = async () => {
        const fileName = name ? name.toLowerCase().replace(/\s+/g, '-') : 'profile';
        try {
            await exportProfileCardToPDF(
                { name, initials, candidateId, status, experience, source, profile, summaryItems },
                fileName
            );
        } catch (err) {
            console.error("PDF export failed:", err);
            const profileText = [
                name,
                `Candidate ID: ${candidateId || '—'}`,
                `Role: ${profile.role}`,
                `Location: ${profile.location}`,
                `Status: ${status || '—'}`,
                `Experience: ${experience || '—'}`,
                `Source: ${source || '—'}`
            ].join('\n');
            downloadBlob(
                new Blob([profileText], { type: 'text/plain' }),
                `${fileName}.txt`
            );
        }
    };

    const handleCardClick = (e) => {
        // Prevent opening profile modal if clicking the checkbox or its container
        if (e.target.closest('.tpc-checkbox-container') || e.target.closest('.tpc-checkbox')) {
            return;
        }
        handleViewProfileClick();
    };

    // Render full career timeline if available, otherwise current + prev org
    const renderCareerJourney = () => {
        const rawTimeline = profile.career_timeline || profile.career;
        const timeline = getNormalizedCareerTimeline(rawTimeline, profile.currentOrg || profile.current || company);
        if (Array.isArray(timeline) && timeline.length > 0) {
            return timeline.map((item, index) => {
                const comp = item.company || item.organization || item.org || '';
                const r = item.role || item.designation || '';
                const ten = item.tenure || item.duration || '';
                const label = item.label;
                const detailText = [r, comp, ten].filter(Boolean).join(' — ');

                return (
                    <div key={index} className="tpc-profile-career-item">
                        <div className="tpc-profile-career-label">{label}</div>
                        <p>{detailText || '—'}</p>
                    </div>
                );
            });
        }

        return (
            <>
                <div className="tpc-profile-career-item">
                    <div className="tpc-profile-career-label">Current org</div>
                    <p>{profile.currentOrg}</p>
                </div>
                <div className="tpc-profile-career-item">
                    <div className="tpc-profile-career-label">Prev organisation</div>
                    <p>{profile.prevOrg || profile.previousOrg || '—'}</p>
                </div>
            </>
        );
    };

    return (
        <div className={`talent-pulse-card ${checked ? 'selected' : ''}`} onClick={handleCardClick}>
            <div className="tpc-header">
                <div className="tpc-header-left">
                    <div className="tpc-avatar">{initials}</div>
                    <div className="tpc-info">
                        <div className="tpc-name">{name}</div>
                        <div className="tpc-badges">
                            <span className="tpc-badge status">{status || 'Profile'}</span>
                        </div>
                    </div>
                </div>
                <label
                    className="tpc-checkbox-container"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${name || 'candidate'}`}
                >
                    <input
                        type="checkbox"
                        className="tpc-checkbox"
                        checked={checked}
                        onChange={(e) => onCheckChange(id, e.target.checked)}
                    />
                </label>
            </div>

            <div className="tpc-details">
                {candidateId && <div className="tpc-detail-id">{candidateId}</div>}
                <div>
                    {details.map((detail, index) => (
                        <div key={index} className="tpc-detail-item">
                            {detail.icon && <span className="tpc-detail-icon">{detail.icon}</span>}
                            <span>{detail.text}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="tpc-grid">
                <div className="tpc-grid-item">
                    <div className="tpc-grid-label">Company</div>
                    <div className="tpc-grid-value">{company || '—'}</div>
                </div>
                <div className="tpc-grid-item">
                    <div className="tpc-grid-label">Experience</div>
                    <div className="tpc-grid-value">{experience || '—'}</div>
                </div>
                <div className="tpc-grid-item">
                    <div className="tpc-grid-label">Source</div>
                    <div className="tpc-grid-value">{source || '—'}</div>
                </div>
            </div>

            <div className="tpc-footer">
                <button className="tpc-view-link" onClick={(e) => { e.stopPropagation(); handleViewProfileClick(); }}>
                    View full profile
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {isModalOpen && (
                // Stop click from bubbling back up to the card div
                <div onClick={(e) => e.stopPropagation()}>
                    <Modal open={isModalOpen} onClose={handleCloseModal} className="tpc-profile-modal">
                        <div className="tpc-profile-modal-header">
                            <div className="tpc-profile-identity">
                                <div className="tpc-profile-avatar">{initials}</div>
                                <div>
                                    <div className="tpc-profile-name-row">
                                        <h2>{name}</h2>
                                        <span className="tpc-badge candidate">CANDIDATE</span>
                                        {candidateId && <span className="tpc-profile-id">{candidateId}</span>}
                                    </div>
                                    <p>{profile.role}</p>
                                    <p className="tpc-profile-location">{profile.location}</p>
                                </div>
                            </div>
                            <div className="tpc-header-actions">
                                <button type="button" className="tpc-download-button" onClick={handleDownloadProfile}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                        <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v3h16v-3" />
                                    </svg>
                                    {constants.DOWNLOAD || "Download"}
                                </button>
                                <button type="button" className="tpc-close-button" onClick={handleCloseModal} aria-label="Close modal">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" width="20" height="20">
                                        <path d="M18 6L6 18M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="tpc-profile-modal-body" ref={modalContentRef}>
                            {isProfileLoading ? (
                                <div className="tpc-profile-skeleton">
                                    <div className="tpc-skeleton-bar tpc-skeleton-bar--wide" />
                                    <div className="tpc-skeleton-bar tpc-skeleton-bar--medium" />
                                    <div className="tpc-skeleton-bar tpc-skeleton-bar--narrow" />
                                    <div className="tpc-skeleton-grid">
                                        <div className="tpc-skeleton-block" />
                                        <div className="tpc-skeleton-block" />
                                    </div>
                                    <div className="tpc-skeleton-bar tpc-skeleton-bar--wide" />
                                    <div className="tpc-skeleton-bar tpc-skeleton-bar--medium" />
                                    <div className="tpc-skeleton-grid">
                                        <div className="tpc-skeleton-block" />
                                        <div className="tpc-skeleton-block" />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="tpc-profile-summary">
                                        {summaryItems.map(({ label, value }) => (
                                            <ProfileSummaryItem key={label} label={label} value={value} />
                                        ))}
                                    </div>

                                    <section className="tpc-profile-section tpc-profile-career">
                                        <h3>Career journey</h3>
                                        <div className="tpc-career-timeline-container">
                                            {renderCareerJourney()}
                                        </div>
                                    </section>

                                    <div className="tpc-profile-two-column">
                                        <ProfileListSection title="Insights from interview" items={profile.insights} />
                                        <ProfileListSection title="Strategy & approach" items={profile.strategy} />
                                    </div>

                                    <div className="tpc-profile-two-column">
                                        <section className="tpc-profile-section">
                                            <h3>Compensation</h3>
                                            <ProfileKeyValueRow label="Current CTC" value={profile.compensation?.current || '—'} />
                                            <ProfileKeyValueRow label="Expected" value={profile.compensation?.expected || '—'} />
                                        </section>
                                        <section className="tpc-profile-section">
                                            <h3>Status & next step</h3>
                                            <ProfileKeyValueRow label="Status" value={status || '—'} />
                                            <ProfileKeyValueRow label="Next" value={profile.nextStep} />
                                        </section>
                                    </div>

                                    <section className="tpc-profile-section">
                                        <h3>Source</h3>
                                        <p>{source || 'Not available'}</p>
                                    </section>
                                </>
                            )}
                        </div>
                    </Modal>
                </div>
            )}
        </div>
    );
};

export default TalentPulseProfileCard;
